import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const EditCaseStudyModal = ({ caseStudy, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    difficulty: 'simple',
    tags: '',
    discussion_period_days: 14,
    solution_content: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (caseStudy && isOpen) {
      setFormData({
        title: caseStudy.title || '',
        description: caseStudy.description || '',
        content: caseStudy.content || '',
        difficulty: caseStudy.difficulty || 'simple',
        tags: caseStudy.tags ? caseStudy.tags.join(', ') : '',
        discussion_period_days: calculateDaysLeft(caseStudy.discussion_period_ends) || 14,
        solution_content: caseStudy.solution_content || ''
      });
    }
  }, [caseStudy, isOpen]);

  const calculateDaysLeft = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const payload = {
        title: formData.title,
        description: formData.description,
        content: formData.content,
        difficulty: formData.difficulty,
        tags: tagsArray,
        discussion_period_days: parseInt(formData.discussion_period_days),
        solution_content: formData.solution_content
      };

      await api.put(`/case-studies/${caseStudy.id}`, payload);
      toast.success('Case study updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating case study:', error);
      toast.error(error.response?.data?.detail || 'Failed to update case study');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Edit Case Study</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
              placeholder="Enter case study title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              rows="3"
              required
              placeholder="Brief description of the case study"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Case Study Content <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
              rows="10"
              required
              placeholder="Enter the full case study content..."
            />
          </div>

          {/* Solution Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Solution Content
            </label>
            <textarea
              value={formData.solution_content}
              onChange={(e) => setFormData({ ...formData, solution_content: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
              rows="8"
              placeholder="Enter the solution (optional, can be added later)"
            />
          </div>

          {/* Difficulty and Discussion Period */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Difficulty <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              >
                <option value="simple">Simple</option>
                <option value="complex">Complex</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discussion Period (days) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.discussion_period_days}
                onChange={(e) => setFormData({ ...formData, discussion_period_days: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                min="1"
                max="90"
                required
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="e.g., diagnosis, acupuncture, herbs"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Case Study'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCaseStudyModal;
